import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Route } from '~/Constants/Routes.constants';
import { Menu, MenuItem } from '~/UIKit';
import { LIGHT_GRAY_BACKGROUND, DISABLED } from '~/Constants/Colors.constants';
import { AccessList, useHasAccess, useProvider } from '~/Hooks';
import { FONT_SIZE_SMALLISH } from '~/Constants/UI.constants';
import { makeStyles } from '~/UIKit/Style';

const useStyles = makeStyles(() => ({
  menuItem: {
    width: '100%',
    fontSize: FONT_SIZE_SMALLISH,
    height: 45,
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: LIGHT_GRAY_BACKGROUND,
    },
  },
  menuItemDisabled: {
    width: '100%',
    fontSize: FONT_SIZE_SMALLISH,
    height: 45,
    backgroundColor: DISABLED,
    cursor: 'default',
    '&:hover': {
      backgroundColor: DISABLED,
    },
  },
  menuWrapper: {
    borderRadius: 0,
  },
}));

export interface IAccountMenuItemsProps {
  anchorEl: null | HTMLElement;
  onClose: () => void;
  onLogout: () => void;
  onNavigate: (path: string) => void;
}

export const AccountMenuItems = (props: IAccountMenuItemsProps) => {
  const { anchorEl, onClose, onLogout, onNavigate } = props;
  const { classes } = useStyles();
  const { hasAccess } = useHasAccess();
  const { t } = useTranslation();
  const { currentProvider } = useProvider();
  const menuItems = useMemo(
    () => [
      {
        hasAccess:
          currentProvider?.organisationVenueId !== undefined &&
          hasAccess(AccessList.VENUEID_IN_MENU),
        label: `#${currentProvider?.organisationVenueId}`,
        testId: 'userMenu.venueId',
      },
      {
        hasAccess: true,
        label: t('app.user.settings'),
        testId: 'userMenu.settings',
        onClick: () => onNavigate(Route.USER_SETTINGS),
      },
      {
        hasAccess: hasAccess(Route.PROVIDER_SELECT),
        label: t('app.changeStudio'),
        testId: 'userMenu.changeStudio',
        onClick: () => onNavigate(Route.PROVIDER_SELECT),
      },
      {
        hasAccess: true,
        label: t('app.logout'),
        testId: 'userMenu.logout',
        onClick: onLogout,
      },
    ],
    [t, hasAccess],
  );

  return (
    <Menu
      anchorEl={anchorEl}
      id="profile-menu"
      keepMounted
      open={Boolean(anchorEl)}
      onClose={onClose}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      className={classes.menuWrapper}
      classes={{ paper: classes.menuWrapper }}
      disableAutoFocusItem
      MenuListProps={{ disablePadding: true }}
    >
      {menuItems.map((menuItem, index) => {
        const { label, hasAccess, onClick, testId } = menuItem;
        if (hasAccess) {
          return (
            <MenuItem
              key={index}
              className={onClick ? classes.menuItem : classes.menuItemDisabled}
              onClick={onClick}
              data-testid={testId}
            >
              {label}
            </MenuItem>
          );
        }
        return null;
      })}
    </Menu>
  );
};
